import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Sparkles, X, RefreshCw, Check } from 'lucide-react';
import { API_BASE } from '../config/api';

const DEFAULT_COLORS = ['#3b82f6', '#0f172a', '#f8fafc'];

/**
 * Modal Mestra: monta layout (assembler) e opcionalmente personaliza com LLM.
 * Não deve ser renderizado para usuários sem aiLibraryCurator.
 */
export default function AiLayoutGenerateModal({ open, authToken, onClose, onApply }) {
  const { t } = useTranslation('builder');
  const [prompt, setPrompt] = useState('');
  const [colors, setColors] = useState([...DEFAULT_COLORS]);
  const [sectionCount, setSectionCount] = useState(4);
  const [useLlm, setUseLlm] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }),
    [authToken]
  );

  const assemble = async () => {
    if (!authToken) return;
    setBusy(true);
    setError('');
    try {
      const endpoint = useLlm ? `${API_BASE}/ai-layout/generate` : `${API_BASE}/ai-layout/assemble`;
      const r = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          prompt: prompt.trim(),
          colors,
          sectionCount,
          mediaPolicy: 'selective',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
      setResult(d);
    } catch (e) {
      setResult(null);
      setError(e.message || 'Erro ao montar layout.');
    } finally {
      setBusy(false);
    }
  };

  const applyToStage = () => {
    const rows = result?.schema?.rows;
    if (!Array.isArray(rows) || !rows.length) {
      setError(t('aiLayoutEmptyResult', { defaultValue: 'Nenhum layout montado ainda. Clique em Montar.' }));
      return;
    }
    onApply?.({
      rows,
      headerWidgets: result.schema.headerWidgets || [],
      globalConfig: result.schema.globalConfig || {},
      meta: result.meta || {},
    });
  };

  if (!open) return null;

  const picked = Array.isArray(result?.meta?.picked) ? result.meta.picked : [];
  const llmMeta = result?.meta?.llm;

  return createPortal(
    <div className="fixed inset-0 z-[2100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-layout-generate-title"
        className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles size={18} className="text-violet-600 dark:text-violet-400 shrink-0" />
            <div className="min-w-0">
              <h2 id="ai-layout-generate-title" className="text-base font-bold text-slate-800 dark:text-slate-100 truncate">
                {t('aiLayoutTitle', { defaultValue: 'Gerar layout (Mestra)' })}
              </h2>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {useLlm
                  ? t('aiLayoutSubtitleLlm', {
                      defaultValue: 'Peças Globais + textos via OpenAI (só Mestra).',
                    })
                  : t('aiLayoutSubtitle', {
                      defaultValue: 'Só assembler (sem LLM) — só conta Mestra.',
                    })}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
            aria-label={t('close', { defaultValue: 'Fechar' })}
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[min(70vh,560px)] overflow-y-auto">
          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              {t('aiLayoutPrompt', { defaultValue: 'Briefing / tema' })}
            </label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={3}
              placeholder={t('aiLayoutPromptPh', {
                defaultValue: 'Ex.: curso introdutório de Excel para negócios, tom profissional…',
              })}
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>

          <label className="flex items-start gap-2.5 rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/50 dark:bg-violet-950/20 px-3 py-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={useLlm}
              onChange={(e) => setUseLlm(e.target.checked)}
              className="mt-0.5 accent-violet-600"
            />
            <span className="min-w-0">
              <span className="block text-xs font-semibold text-violet-800 dark:text-violet-200">
                {t('aiLayoutUseLlm', { defaultValue: 'Montar com IA (textos + densidade)' })}
              </span>
              <span className="block text-[10px] text-slate-500 dark:text-slate-400 mt-0.5">
                {t('aiLayoutUseLlmHint', {
                  defaultValue: 'Requer chave OpenAI em Master → Configurações. Sem marcar = só encaixa seções.',
                })}
              </span>
            </span>
          </label>

          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              {t('aiLayoutColors', { defaultValue: 'Cores (primária, secundária, fundo)' })}
            </label>
            <div className="flex gap-3">
              {colors.map((c, i) => (
                <label key={i} className="flex flex-col items-center gap-1 flex-1">
                  <input
                    type="color"
                    value={c}
                    onChange={(e) => {
                      const next = [...colors];
                      next[i] = e.target.value;
                      setColors(next);
                    }}
                    className="w-full h-10 rounded-lg border border-slate-200 dark:border-slate-600 cursor-pointer bg-transparent"
                  />
                  <span className="text-[10px] font-mono text-slate-400">{c}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 dark:text-slate-300 mb-1">
              {t('aiLayoutSectionCount', { defaultValue: 'Número de seções' })}: {sectionCount}
            </label>
            <input
              type="range"
              min={1}
              max={10}
              value={sectionCount}
              onChange={(e) => setSectionCount(Number(e.target.value))}
              className="w-full accent-violet-600"
            />
            <div className="flex justify-between text-[10px] text-slate-400">
              <span>1</span>
              <span>10</span>
            </div>
          </div>

          {error ? (
            <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded-lg px-3 py-2">
              {error}
            </p>
          ) : null}

          {picked.length > 0 ? (
            <div className="rounded-xl border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/30 p-3">
              <p className="text-[11px] font-semibold text-violet-800 dark:text-violet-200 mb-1.5">
                {t('aiLayoutPicked', {
                  defaultValue: 'Seções escolhidas (última montagem)',
                })}
              </p>
              <ul className="space-y-1">
                {picked.map((p, idx) => (
                  <li key={`${p.id}-${idx}`} className="text-[11px] text-slate-600 dark:text-slate-300 truncate">
                    <span className="font-semibold text-violet-600 dark:text-violet-300">{p.section_role}</span>
                    {' · '}
                    {p.name}
                  </li>
                ))}
              </ul>
              {result?.meta?.tags?.length ? (
                <p className="text-[10px] text-slate-400 mt-2 truncate">
                  tags: {result.meta.tags.join(', ')}
                </p>
              ) : null}
              {llmMeta?.used ? (
                <p className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1">
                  LLM {llmMeta.model}
                  {llmMeta.courseTitle ? ` · “${llmMeta.courseTitle}”` : ''}
                  {typeof llmMeta.slotsApplied === 'number'
                    ? ` · ${llmMeta.slotsApplied} textos`
                    : ''}
                  {typeof llmMeta.coverage === 'number'
                    ? ` · ${Math.round(llmMeta.coverage * 100)}% via IA`
                    : ''}
                  {llmMeta.density && llmMeta.density !== 'normal' ? ` · ${llmMeta.density}` : ''}
                </p>
              ) : null}
              {typeof result?.meta?.heroUseBg === 'boolean' ? (
                <p className="text-[10px] text-slate-500 mt-1">
                  Hero com imagem de fundo: {result.meta.heroUseBg ? 'sim' : 'não (aleatório nesta geração)'}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="px-5 py-4 border-t border-slate-100 dark:border-slate-700 flex flex-wrap gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-sm font-semibold rounded-lg border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
          >
            {t('cancel', { defaultValue: 'Cancelar' })}
          </button>
          <button
            type="button"
            onClick={assemble}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : result ? <RefreshCw size={14} /> : <Sparkles size={14} />}
            {result
              ? t('aiLayoutRegenerate', { defaultValue: 'Regenerar' })
              : useLlm
                ? t('aiLayoutAssembleLlm', { defaultValue: 'Montar com IA' })
                : t('aiLayoutAssemble', { defaultValue: 'Montar' })}
          </button>
          <button
            type="button"
            onClick={applyToStage}
            disabled={busy || !result?.schema?.rows?.length}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Check size={14} />
            {t('aiLayoutApply', { defaultValue: 'Aplicar no palco' })}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
